import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, ArrowLeft, ClipboardCheck, Sunrise, Sunset, Sun, Phone } from "lucide-react";
import { parseLocalDate, todayLocalIso } from "@/lib/dateUtils";

const SLOT_META = {
  morning:   { label: "Morning",   short: "AM", Icon: Sunrise },
  afternoon: { label: "Afternoon", short: "PM", Icon: Sunset },
  all_day:   { label: "All day",   short: "ALL", Icon: Sun },
};

const PRIORITY_LABEL = { low: "Low", normal: "Normal", high: "High", urgent: "Urgent" };
const PRIORITY_PRINT = { low: "·", normal: "", high: "★", urgent: "★★" };

const todayIso = () => todayLocalIso();

export default function HelperTasksPrint() {
  const navigate = useNavigate();
  const { organization } = useAuth();
  const [params, setParams] = useSearchParams();
  const dateParam = params.get("date") || todayIso();

  const [helpers, setHelpers] = useState([]);
  const [units, setUnits] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tk, cl, un] = await Promise.all([
        api.get(`/housing/helper-tasks?expand=true&from_date=${dateParam}&to_date=${dateParam}`),
        api.get("/housing/cleaners"),
        api.get("/housing/units"),
      ]);
      // expand=true returns {from_date, to_date, occurrences, tasks}
      // Use occurrences (per-day expansions of recurring rules) as the source of truth.
      const occ = tk.data?.occurrences || tk.data || [];
      setTasks(Array.isArray(occ) ? occ : []);
      setHelpers(cl.data || []);
      setUnits(un.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [dateParam]);
  useEffect(() => { load(); }, [load]);

  const unitById = useMemo(() => Object.fromEntries(units.map((u) => [u.id, u])), [units]);
  const activeHelpers = useMemo(() => helpers.filter((h) => h.active !== false), [helpers]);

  // Group tasks by helper_id; keep an "Unassigned" bucket for orphans.
  const grouped = useMemo(() => {
    const buckets = new Map();
    activeHelpers.forEach((h) => buckets.set(h.id, { helper: h, tasks: [] }));
    buckets.set("__unassigned__", { helper: { id: "__unassigned__", name: "UNASSIGNED — needs an owner", phone: "" }, tasks: [] });
    tasks.forEach((t) => {
      const k = t.helper_id || "__unassigned__";
      if (!buckets.has(k)) {
        // assigned to an inactive/deleted helper — surface them in their own group
        const h = helpers.find((x) => x.id === t.helper_id);
        buckets.set(k, { helper: h || { id: k, name: "(unknown)", phone: "" }, tasks: [] });
      }
      buckets.get(k).tasks.push(t);
    });
    // Sort tasks within each bucket: morning → afternoon → all_day, then priority high→low
    const slotOrder = { morning: 0, afternoon: 1, all_day: 2 };
    const prioOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
    Array.from(buckets.values()).forEach((b) => {
      b.tasks.sort((a, c) =>
        (slotOrder[a.slot || "all_day"] - slotOrder[c.slot || "all_day"]) ||
        (prioOrder[a.priority || "normal"] - prioOrder[c.priority || "normal"])
      );
    });
    // Drop empty buckets unless it's an active helper (we still print empty ones so the supervisor can hand them their sheet)
    const out = Array.from(buckets.values()).filter((b) => b.tasks.length > 0 || b.helper.id !== "__unassigned__");
    out.sort((a, b) => a.helper.name.localeCompare(b.helper.name));
    return out;
  }, [tasks, activeHelpers, helpers]);

  const totalTasks = tasks.length;
  const dt = parseLocalDate(dateParam);
  const humanDate = isNaN(dt.getTime())
    ? dateParam
    : dt.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  const orgTitle = organization?.display_name || "Mission Hospitality";

  return (
    <div className="bg-white min-h-screen text-[#1C1F1E]" data-testid="roster-print-page">
      {/* Print-only style overrides */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .helper-block { page-break-inside: avoid; break-inside: avoid; }
          .new-helper-page { page-break-after: always; }
          body { background: white !important; }
        }
        @page {
          size: A4 portrait;
          margin: 14mm;
        }
      `}</style>

      {/* Toolbar — hidden in print */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-[#E8E8E6] px-6 py-3 flex flex-wrap items-center gap-3 justify-between" data-testid="print-toolbar">
        <Button variant="ghost" size="sm" onClick={() => navigate("/helper-tasks")} className="text-[#525C58]" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to tasks
        </Button>
        <div className="flex items-center gap-2">
          <label className="text-xs text-[#525C58]">Date:</label>
          <Input
            type="date"
            value={dateParam}
            onChange={(e) => setParams({ date: e.target.value || todayIso() })}
            className="h-8 w-44"
            data-testid="print-date"
          />
          <label className="text-xs text-[#525C58] ml-2">
            <input
              type="checkbox"
              className="mr-1 align-middle"
              onChange={(e) => {
                const els = document.querySelectorAll(".helper-block");
                els.forEach((el) => el.classList.toggle("new-helper-page", e.target.checked));
              }}
              data-testid="one-per-page-toggle"
            />
            One sheet per helper
          </label>
          <Button onClick={() => window.print()} className="bg-[#1B4332] hover:bg-[#143526]" data-testid="print-btn">
            <Printer className="h-4 w-4 mr-1" /> Print roster
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-8 py-6">
        {/* Header */}
        <header className="mb-6 pb-4 border-b-2 border-[#1B4332]" data-testid="roster-header">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#525C58] font-semibold">{orgTitle}</p>
          <h1 className="text-3xl font-semibold tracking-tight mt-1 flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            <ClipboardCheck className="h-7 w-7 text-[#40916C]" />
            Daily Helper Roster
          </h1>
          <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#1C1F1E]">
            <span><strong>{humanDate}</strong></span>
            <span>{totalTasks} task{totalTasks !== 1 ? "s" : ""} across {grouped.length} helper sheet{grouped.length !== 1 ? "s" : ""}</span>
          </div>
        </header>

        {loading ? (
          <p className="text-center py-10 text-[#9AA39E]" data-testid="loading">Loading roster…</p>
        ) : grouped.length === 0 ? (
          <p className="text-center py-10 text-[#9AA39E]" data-testid="empty">No tasks scheduled — and no active helpers. Add a helper first.</p>
        ) : (
          <div className="space-y-8">
            {grouped.map(({ helper, tasks: bucket }) => (
              <section key={helper.id} className="helper-block" data-testid={`helper-${helper.id}`}>
                <div className="flex items-end justify-between border-b border-[#E8E8E6] pb-1 mb-3">
                  <div>
                    <h2 className="text-xl font-semibold text-[#1B4332]">{helper.name}</h2>
                    {helper.phone && (
                      <p className="text-xs text-[#525C58] flex items-center gap-1 mt-0.5">
                        <Phone className="h-3 w-3" /> {helper.phone}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-[#9AA39E]">Tasks today</p>
                    <p className="text-2xl font-semibold text-[#1B4332]">{bucket.length}</p>
                  </div>
                </div>

                {bucket.length === 0 ? (
                  <p className="text-xs text-[#9AA39E] italic py-2">No tasks scheduled for {humanDate}.</p>
                ) : (
                  <table className="w-full text-sm" data-testid={`helper-table-${helper.id}`}>
                    <thead>
                      <tr className="border-b border-[#E8E8E6] text-[10px] uppercase tracking-wider text-[#525C58]">
                        <th className="text-left py-1 w-10">Done</th>
                        <th className="text-left py-1 w-14">Slot</th>
                        <th className="text-left py-1">Task</th>
                        <th className="text-left py-1 w-32">Unit</th>
                        <th className="text-center py-1 w-10">Pri</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bucket.map((t) => {
                        const slot = SLOT_META[t.slot || "all_day"];
                        return (
                          <tr key={t.task_id || t.id} className="border-b border-dashed border-[#E8E8E6] align-top">
                            <td className="py-2">
                              <span className="inline-block w-5 h-5 border-2 border-[#1B4332] rounded-sm align-middle" aria-label="tick box" />
                            </td>
                            <td className="py-2 text-xs font-semibold">{slot?.short}</td>
                            <td className="py-2">
                              <p className="font-medium leading-tight">{t.title}</p>
                              {t.description && <p className="text-[11px] text-[#525C58] mt-0.5 leading-snug">{t.description}</p>}
                              {(t.start_time || t.end_time) && (
                                <p className="text-[10px] text-[#7A8A84] mt-0.5">{t.start_time}{t.end_time ? ` → ${t.end_time}` : ""}</p>
                              )}
                              {t.notes && <p className="text-[11px] text-[#7A8A84] italic mt-0.5">Notes: {t.notes}</p>}
                            </td>
                            <td className="py-2 text-xs">{t.unit_id ? (unitById[t.unit_id]?.name || "—") : "—"}</td>
                            <td className="py-2 text-center text-sm" title={PRIORITY_LABEL[t.priority || "normal"]}>
                              {PRIORITY_PRINT[t.priority || "normal"] || ""}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                {/* Signature footer */}
                <div className="mt-4 grid grid-cols-3 gap-6 text-[10px] text-[#7A8A84]">
                  <div>
                    <p className="border-t border-[#1C1F1E] pt-1">Helper signature</p>
                  </div>
                  <div>
                    <p className="border-t border-[#1C1F1E] pt-1">Supervisor signature</p>
                  </div>
                  <div>
                    <p className="border-t border-[#1C1F1E] pt-1">Time completed</p>
                  </div>
                </div>
              </section>
            ))}
          </div>
        )}

        <footer className="no-print mt-10 text-center text-[10px] text-[#9AA39E]">
          Tip: tick "One sheet per helper" before printing to give each helper their own page.
        </footer>
      </div>
    </div>
  );
}
